import { useFilterQuery } from 'dashboard/components-next/filter-bar/useFilterQuery';
import { resolvePeriod } from 'dashboard/components-next/filter-bar/period';

// UI-FILTROS-01 (UF-43, D7): quem lê e escreve o link dos relatórios é o useFilterQuery, com o
// esquema deles. Estas funções ficam como invólucro, com os nomes e o formato de antes (o spec
// delas é o gabarito): os números saem como número, e o período sai com as datas.

// Com o nome e as datas que a URL traz, lidos pelo `resolvePeriod`, que mora com o PeriodPicker
// (UI-FILTROS-01 §2.4): uma cópia só para o link e para o seletor. O `periodCodec` do period.js não
// serve aqui: ele trocaria por personalizado o nome rolante com as datas de outro dia, que estes
// relatórios guardam como vieram, e escreveria o nome sem as datas.
// O `now` de quem lê chega ao codec junto com a query, numa chave que não vira parâmetro.
const NOW = Symbol('now');
const first = raw => [raw].flat()[0];
const unixOrNull = raw => Number(first(raw)) || null;
const period = {
  param: ['range', 'from', 'to'],
  read: query =>
    resolvePeriod(
      first(query.range),
      unixOrNull(query.from),
      unixOrNull(query.to),
      query[NOW] ?? new Date()
    ),
  write: ({ range, from, to } = {}) => ({
    from: from || undefined,
    to: to || undefined,
    range: range || undefined,
  }),
};

// O id é lido pelo tipo do useFilterQuery, e escrito como veio: o tipo o escreveria como texto.
const id = param => ({
  param,
  type: 'id',
  default: null,
  write: value => ({ [param]: value || undefined }),
});

// `agent_id` é o atendente; o agente de IA da página Atendimento é `power_agent_id`.
const REPORT_SCHEMA = {
  period,
  businessHours: { param: 'business_hours', type: 'bool', default: false },
  groupBy: { ...id('group_by'), type: 'int' },
  agent_id: id('agent_id'),
  power_agent_id: id('power_agent_id'),
  inbox_id: id('inbox_id'),
  team_id: id('team_id'),
  sla_policy_id: id('sla_policy_id'),
  label: { param: 'label', type: 'text', default: null },
  rating: id('rating'),
};

const FILTER_KEYS = [
  'agent_id',
  'power_agent_id',
  'inbox_id',
  'team_id',
  'sla_policy_id',
  'label',
  'rating',
];

const { toQuery, fromQuery } = useFilterQuery(REPORT_SCHEMA);

const pick = (object, keys) =>
  Object.fromEntries(keys.map(key => [key, object?.[key]]));

export const generateReportURLParams = ({
  from,
  to,
  businessHours,
  groupBy,
  range,
}) => toQuery({ period: { range, from, to }, businessHours, groupBy });

export const parseReportURLParams = (query, now = new Date()) => {
  const {
    period: { range, from, to },
    businessHours,
    groupBy,
  } = fromQuery({ ...query, [NOW]: now });
  return { range, from, to, businessHours, groupBy };
};

// O backend grava 0 no tempo em horário comercial da caixa sem horário
// configurado. Sem nenhuma caixa com horário no recorte (a caixa filtrada, ou a
// conta inteira sem filtro de caixa), o interruptor trocaria a medida por zeros.
export const hasBusinessHoursInboxes = (inboxes = [], inboxId = null) =>
  inboxes.some(
    inbox =>
      inbox.working_hours_enabled && (!inboxId || inbox.id === Number(inboxId))
  );

// Parse filter params from URL (agent_id, inbox_id, team_id, sla_policy_id, label, rating).
export const parseFilterURLParams = query =>
  pick(fromQuery(query), FILTER_KEYS);

// Generate filter URL params (only include non-null values)
export const generateFilterURLParams = filters =>
  toQuery(pick(filters, FILTER_KEYS));

// Merge date range and filter params for complete URL
export const generateCompleteURLParams = ({
  from,
  to,
  range,
  filters = {},
}) => {
  const dateParams = generateReportURLParams({ from, to, range });
  const filterParams = generateFilterURLParams(filters);
  return { ...dateParams, ...filterParams };
};
